import React, { FC, PropsWithChildren, useEffect, useMemo, useRef } from "react";
import { Spinner } from "@lookiero/aurora";
import { QueryStatus } from "@lookiero/messaging-react";
import { useViewStyleProfileNextBestActionDefinitionByCustomerId } from "../../../projection/nextBestActionDefinition/react/useViewStyleProfileNextBestActionDefinitionByCustomerId";
import { useViewStyleProfileAnswersByCustomerId } from "../../../projection/styleProfileAnswers/react/useViewStyleProfileAnswersByCustomerId";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { NextBestActionForm } from "./components/nextBestActionForm/NextBestActionForm";
import { NextBestActionSuccess } from "./components/nextBestActionSuccess/NextBestActionSuccess";

interface OnNextBestActionReadyFunctionArgs {
  readonly actions: number;
}

interface OnNextBestActionReadyFunction {
  (args: OnNextBestActionReadyFunctionArgs): void;
}

interface OnNextBestActionCompletedFunction {
  (): void;
}

interface OnNextBestActionNavigateToStyleProfileFunction {
  (): void;
}

interface OnNextBestActionPressCta {
  (): void;
}

interface NextBestActionScreenProps {
  readonly screenModalProviders: FC<PropsWithChildren>;
  readonly onReady: OnNextBestActionReadyFunction;
  readonly onCompleted: OnNextBestActionCompletedFunction;
  readonly onNavigateToStyleProfile: OnNextBestActionNavigateToStyleProfileFunction;
  readonly onPressCta: OnNextBestActionPressCta;
}

const NextBestActionScreen: FC<NextBestActionScreenProps> = ({
  screenModalProviders,
  onReady,
  onCompleted,
  onNavigateToStyleProfile,
  onPressCta,
}) => {
  const {
    basePath,
    tradename,
    customer: { customerId, country, segment },
  } = useStaticInfo();

  const [styleProfileNextBestActionDefinition] = useViewStyleProfileNextBestActionDefinitionByCustomerId({
    customerId,
  });
  const [answers, answersStatus] = useViewStyleProfileAnswersByCustomerId({ customerId });

  const actions = useMemo(() => styleProfileNextBestActionDefinition || [], [styleProfileNextBestActionDefinition]);

  const initialActionsLength = useRef<number | undefined>(undefined);
  if (styleProfileNextBestActionDefinition && initialActionsLength.current === undefined) {
    initialActionsLength.current = actions.length;
  }

  const hasActions = Boolean(initialActionsLength.current);

  const onReadyCalled = useRef(false);
  const onCompletedCalled = useRef(false);
  useEffect(() => {
    if (actions.length > 0 && !onReadyCalled.current) {
      onReadyCalled.current = true;
      onReady({ actions: actions.length });
    }

    if (hasActions && actions.length === 0 && !onCompletedCalled.current) {
      onCompletedCalled.current = true;
      onCompleted();
    }
  }, [hasActions, actions.length, onCompleted, onReady]);

  const finished = actions.length === 0;
  const answersLoaded = answersStatus === QueryStatus.SUCCESS || Boolean(answers);

  if (!styleProfileNextBestActionDefinition || !answersLoaded) {
    return <Spinner testID="spinner" />;
  }

  return finished && hasActions ? (
    <NextBestActionSuccess onNavigateToStyleProfile={onNavigateToStyleProfile} />
  ) : finished ? null : (
    <NextBestActionForm
      actions={actions}
      answers={answers || {}}
      basePath={basePath}
      country={country}
      customerId={customerId}
      screenModalProviders={screenModalProviders}
      segment={segment}
      tradename={tradename}
      onPressCta={onPressCta}
    />
  );
};

export type {
  OnNextBestActionReadyFunction,
  OnNextBestActionCompletedFunction,
  OnNextBestActionNavigateToStyleProfileFunction,
  OnNextBestActionPressCta,
};
export { NextBestActionScreen };
